// Saved connection profiles. Pure logic over a minimal storage interface so it
// can be unit tested without React or a browser. Keys stay in localStorage.

import type { ApiConfig } from "./api";

export const PROFILES_KEY = "growdhi.tenantAdminApi.profiles.v1";
export const LEGACY_CONFIG_KEY = "growdhi.tenantAdminApi.config";

export interface Profile extends ApiConfig {
  id: string;
  name: string;
}

export interface ProfileStore {
  profiles: Profile[];
  activeId: string | null;
}

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type ProfileInput = ApiConfig & { name: string };

function isConfig(v: unknown): v is ApiConfig {
  const o = v as Record<string, unknown> | null;
  return (
    !!o &&
    typeof o.baseUrl === "string" && o.baseUrl !== "" &&
    typeof o.apiKey === "string" && o.apiKey !== "" &&
    typeof o.tenantId === "string" && o.tenantId !== ""
  );
}

function isProfile(v: unknown): v is Profile {
  const o = v as Record<string, unknown> | null;
  return isConfig(v) && typeof o?.id === "string" && typeof o?.name === "string";
}

export function newId(): string {
  return `p_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// fixBaseUrl lets the caller repair retired URLs without this module knowing them.
export function loadStore(
  storage: KeyValueStorage,
  fixBaseUrl: (url: string) => string = (u) => u,
): ProfileStore {
  let store: ProfileStore = { profiles: [], activeId: null };
  try {
    const raw = storage.getItem(PROFILES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const profiles: Profile[] = Array.isArray(parsed?.profiles) ? parsed.profiles.filter(isProfile) : [];
      const activeId = profiles.some((p) => p.id === parsed?.activeId)
        ? (parsed.activeId as string)
        : (profiles[0]?.id ?? null);
      store = { profiles, activeId };
    } else {
      const legacy = storage.getItem(LEGACY_CONFIG_KEY);
      if (legacy) {
        const cfg = JSON.parse(legacy);
        if (isConfig(cfg)) {
          const id = newId();
          store = {
            profiles: [
              { id, name: `${cfg.tenantId} default`, baseUrl: cfg.baseUrl, apiKey: cfg.apiKey, tenantId: cfg.tenantId },
            ],
            activeId: id,
          };
        }
      }
    }
  } catch {
    /* corrupt storage: start empty */
  }
  store = {
    ...store,
    profiles: store.profiles.map((p) => ({ ...p, baseUrl: fixBaseUrl(p.baseUrl) })),
  };
  if (store.profiles.length > 0) saveStore(storage, store);
  return store;
}

// Persists the store. The legacy key mirrors the active profile so an older
// build of this UI still finds a config; it is removed when none is active.
export function saveStore(storage: KeyValueStorage, store: ProfileStore): void {
  storage.setItem(PROFILES_KEY, JSON.stringify(store));
  const active = getActive(store);
  if (active) {
    const { baseUrl, apiKey, tenantId } = active;
    storage.setItem(LEGACY_CONFIG_KEY, JSON.stringify({ baseUrl, apiKey, tenantId }));
  } else {
    storage.removeItem(LEGACY_CONFIG_KEY);
  }
}

export function getActive(store: ProfileStore): Profile | null {
  return store.profiles.find((p) => p.id === store.activeId) ?? null;
}

// New profiles become active.
export function addProfile(store: ProfileStore, input: ProfileInput, id: string = newId()): ProfileStore {
  const profile: Profile = { ...input, id };
  return { profiles: [...store.profiles, profile], activeId: id };
}

export function updateProfile(store: ProfileStore, id: string, input: ProfileInput): ProfileStore {
  return {
    ...store,
    profiles: store.profiles.map((p) => (p.id === id ? { ...input, id } : p)),
  };
}

export function deleteProfile(store: ProfileStore, id: string): ProfileStore {
  const profiles = store.profiles.filter((p) => p.id !== id);
  const activeId = store.activeId === id ? (profiles[0]?.id ?? null) : store.activeId;
  return { profiles, activeId };
}

export function selectProfile(store: ProfileStore, id: string): ProfileStore {
  return store.profiles.some((p) => p.id === id) ? { ...store, activeId: id } : store;
}
